/**
 * Messaging API — thin wrappers over the deployed backend.
 *
 * Backend reference: backend-messaging-R1.md
 *   - Table is `public.message_threads` (NOT `threads`).
 *   - Messages table has `client_temp_id` for optimistic reconcile.
 *   - RPCs available: mark_thread_read, set_thread_stage,
 *     create_guest_thread, send_guest_message, claim_guest_thread.
 *   - Designer-side reads/writes happen via direct authed Supabase calls;
 *     RLS uses public.current_user_id() (JWT sub).
 *   - Guest send/create go through rate-limited Express endpoints
 *     (POST /v1/messaging/guest/*) for abuse protection.
 */
import { supabase } from '@/lib/supabase';
import { apiClient, type ApiErrorResponse, type ApiResponse } from '@/services/apiClient';
import type {
  Message,
  MessageAttachment,
  Stage,
  Thread,
  ThreadParticipant,
} from '../types';

/* ---------- DB row shapes ---------- */

export interface DbThreadClientProfile {
  id: string;
  full_name: string | null;
  fullName?: string | null; // Backend may return camelCase
  username: string | null;
  profile_picture: string | null;
}

export interface DbThread {
  id: string;
  designer_id: string;
  client_id: string | null;
  guest_token: string | null;
  guest_name: string | null;
  guest_email: string | null;
  guest_phone: string | null;
  stage: Stage;
  last_message_at: string | null;
  last_message_preview: string | null;
  unread_for_designer: number;
  unread_for_client: number;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
  /**
   * Optional joined profile for the authenticated client on this thread.
   * Populated by `GET /v1/messaging/threads` (designer inbox endpoint) and
   * absent on Realtime `message_threads` rows which stream the base table
   * only. The store layer carries the previously-hydrated client through
   * subsequent updates so the inbox keeps showing the real name/avatar.
   */
  client?: DbThreadClientProfile | null;
  /**
   * Optional joined profile for the designer on this thread.
   * Populated by `fetchClientThreads` (direct Supabase query with FK join)
   * so the buyer inbox can show the designer's name/avatar.
   * Absent on Realtime rows — the store carries it forward from the cache.
   * Backend may return this as `designer_user`, `designer`, or `designerUser`.
   */
  designer_user?: DbThreadClientProfile | null;
  designer?: DbThreadClientProfile | null;        // camelCase alias
  designerUser?: DbThreadClientProfile | null;    // camelCase alias
}

export interface DbMessage {
  id: string;
  thread_id: string;
  sender_role: 'client' | 'designer' | 'system' | 'ai_summary';
  sender_id: string | null;
  body: string | null;
  attachments: unknown;
  metadata: unknown;
  client_temp_id: string | null;
  created_at: string;
}

/* ---------- Row -> domain mappers ---------- */

const sanitizeAttachments = (raw: unknown): MessageAttachment[] | undefined => {
  if (!Array.isArray(raw) || raw.length === 0) return undefined;
  return raw as MessageAttachment[];
};

const sanitizeMetadata = (raw: unknown): Record<string, unknown> | undefined => {
  if (!raw || typeof raw !== 'object') return undefined;
  const obj = raw as Record<string, unknown>;
  return Object.keys(obj).length === 0 ? undefined : obj;
};

export const mapThread = (
  row: DbThread,
  self: ThreadParticipant,
  selfRole: 'designer' | 'client' = 'designer',
  /**
   * Designer role only: previously-hydrated client participant. Realtime
   * UPDATE rows arrive without the joined `client` field (it only ships from
   * the inbox endpoint), so the store passes the last-known participant
   * through to avoid regressing the inbox label back to 'Client'.
   */
  fallbackClient?: ThreadParticipant | null,
): Thread => {
  let designer: ThreadParticipant;
  let client: ThreadParticipant | null = null;

  if (selfRole === 'client') {
    // Viewer is the buyer/client. The "other party" to display is the designer.
    // Prefer the fresh join from fetchClientThreads; fall back to a
    // placeholder so the inbox has something to render even on Realtime rows
    // (which don't carry the join — store.ts re-injects it from the cache).
    const dp = row.designer_user ?? row.designerUser ?? row.designer ?? null;
    designer = dp
      ? {
          id: row.designer_id,
          fullName: dp.full_name || dp.fullName || 'Designer',
          username: dp.username ?? undefined,
          avatarUrl: dp.profile_picture ?? null,
          isOnline: false,
        }
      : { id: row.designer_id, fullName: 'Designer', avatarUrl: null, isOnline: false };
    // Self is the client participant on this thread.
    client = { ...self };
  } else {
    // Viewer is the designer. Self is the designer; other party is the client.
    designer = self;
    if (row.client_id) {
      if (row.client) {
        client = {
          id: row.client.id,
          fullName: row.client.full_name || row.client.fullName || row.guest_name || 'Client',
          username: row.client.username ?? undefined,
          avatarUrl: row.client.profile_picture ?? null,
          isOnline: false,
        };
      } else if (fallbackClient && fallbackClient.id === row.client_id) {
        client = fallbackClient;
      } else {
        client = {
          id: row.client_id,
          fullName: row.guest_name || 'Client',
          avatarUrl: null,
          isOnline: false,
        };
      }
    }
  }

  return {
    id: row.id,
    designer,
    client,
    guestToken: row.guest_token,
    guestName: row.guest_name,
    guestEmail: row.guest_email,
    guestPhone: row.guest_phone,
    stage: row.stage,
    lastMessageAt: row.last_message_at,
    lastMessagePreview: row.last_message_preview,
    unreadForDesigner: row.unread_for_designer ?? 0,
    unreadForClient: row.unread_for_client ?? 0,
    archivedAt: row.archived_at,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
};

export const mapMessage = (row: DbMessage): Message => ({
  id: row.id,
  threadId: row.thread_id,
  senderRole: row.sender_role,
  senderId: row.sender_id,
  body: row.body ?? '',
  attachments: sanitizeAttachments(row.attachments),
  metadata: sanitizeMetadata(row.metadata),
  createdAt: row.created_at,
  clientTempId: row.client_temp_id ?? undefined,
});

/* ---------- Reads ---------- */

export interface FetchDesignerThreadsOptions {
  /** Max rows per page. Backend caps internally; FE default matches that cap. */
  limit?: number;
  /**
   * Cursor from a previous page — the `last_message_at` of the oldest row in
   * the last batch. Pass `undefined` for the first page.
   */
  cursor?: string;
}

export interface FetchDesignerThreadsResult {
  threads: DbThread[];
  nextCursor: string | null;
}

/**
 * Designer inbox list. Hits the server-side endpoint so the response is
 * pre-joined against `users`, giving us `client.full_name / username /
 * profile_picture` in a single round trip instead of an embedded PostgREST
 * select. Unused `designerId` arg kept for call-site compatibility — the
 * backend derives the designer from the Bearer token.
 */
export async function fetchDesignerThreads(
  _designerId: string,
  options: FetchDesignerThreadsOptions = {},
): Promise<FetchDesignerThreadsResult> {
  const params: Record<string, string | number> = {};
  if (options.limit) params.limit = options.limit;
  if (options.cursor) params.cursor = options.cursor;
  const res = (await apiClient.get('/messaging/threads', params)) as ApiResponse<
    | { threads: DbThread[]; nextCursor?: string | null }
    | DbThread[]
  >;
  if (!res.success) throw new Error(res.error.message);
  // Tolerate either `{ threads, nextCursor }` (spec) or a bare array fallback.
  if (Array.isArray(res.data)) {
    return { threads: res.data, nextCursor: null };
  }
  return {
    threads: res.data.threads ?? [],
    nextCursor: res.data.nextCursor ?? null,
  };
}

/**
 * Buyer/client inbox list. Hits `GET /v1/messaging/client/threads` — the
 * server-side endpoint that queries threads where `client_id = current_user`
 * and pre-joins the designer profile (name, avatar) in one round-trip.
 */
export async function fetchClientThreads(
  options: FetchDesignerThreadsOptions = {},
): Promise<FetchDesignerThreadsResult> {
  const params: Record<string, string | number> = {};
  if (options.limit) params.limit = options.limit;
  if (options.cursor) params.cursor = options.cursor;
  const res = (await apiClient.get('/messaging/client/threads', params)) as ApiResponse<
    | { threads: DbThread[]; nextCursor?: string | null }
    | DbThread[]
  >;
  if (!res.success) throw new Error(res.error.message);
  const threads = Array.isArray(res.data) ? res.data : (res.data.threads ?? []);
  // Debug: surface raw designer fields so we know what the backend returns
  if (threads.length > 0) {
    const sample = threads[0] as any;
    console.debug('[messaging] client/threads raw thread keys:', Object.keys(sample));
    console.debug('[messaging] designer_user:', sample.designer_user, '| designer:', sample.designer, '| designerUser:', sample.designerUser);
  }
  if (Array.isArray(res.data)) {
    return { threads, nextCursor: null };
  }
  return {
    threads,
    nextCursor: res.data.nextCursor ?? null,
  };
}

export async function fetchThread(threadId: string): Promise<DbThread | null> {
  const { data, error } = await supabase
    .from('message_threads')
    .select('*')
    .eq('id', threadId)
    .maybeSingle();
  if (error) throw error;
  return (data as DbThread | null) ?? null;
}

export async function fetchMessages(threadId: string): Promise<DbMessage[]> {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('thread_id', threadId)
    .order('created_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as DbMessage[];
}

/* ---------- Writes ---------- */

export interface InsertDesignerMessageInput {
  threadId: string;
  senderId: string;
  body: string;
  attachments?: MessageAttachment[];
  clientTempId: string;
  metadata?: Record<string, unknown>;
}

export async function insertDesignerMessage(
  input: InsertDesignerMessageInput,
): Promise<DbMessage> {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      thread_id: input.threadId,
      sender_role: 'designer',
      sender_id: input.senderId,
      body: input.body,
      attachments: input.attachments ?? [],
      client_temp_id: input.clientTempId,
      ...(input.metadata ? { metadata: input.metadata } : {}),
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as DbMessage;
}

export async function rpcMarkThreadRead(threadId: string): Promise<void> {
  const { error } = await supabase.rpc('mark_thread_read', {
    p_thread_id: threadId,
  });
  if (error) throw error;
}

export async function rpcSetThreadStage(
  threadId: string,
  stage: Stage,
): Promise<void> {
  const { error } = await supabase.rpc('set_thread_stage', {
    p_thread_id: threadId,
    p_stage: stage,
  });
  if (error) throw error;
}

/* ---------- Guest (Express, rate-limited) ----------
 * These hit the abuse-protected Express endpoints that wrap
 * create_guest_thread / send_guest_message via service role. The frontend
 * never calls those RPCs directly because the rate limits live in Express.
 */

export interface CreateGuestThreadInput {
  designerId: string;
  guestToken: string;
  guestName?: string;
  guestEmail?: string;
  guestPhone?: string;
  /**
   * Free-form thread metadata. Currently used to capture inbound source
   * tracking (e.g. `{ source: { utm_source: 'instagram', ref: 'bio' } }`)
   * so the designer's inbox can show "via Instagram bio". Backend stores
   * this on `threads.metadata` (jsonb).
   */
  metadata?: Record<string, unknown>;
}

export interface SendGuestMessageInput {
  threadId: string;
  guestToken: string;
  body: string;
  attachments?: MessageAttachment[];
  /** Echoes back on the server row + via guest poll for optimistic reconcile (R1.5+). */
  clientTempId?: string;
  /**
   * If set, the server inserts the buyer's message AND a system-flagged
   * auto-reply (the designer's pre-saved reply for this chip) in one
   * transaction. Set when the buyer taps a Quick Reply chip.
   * Synthetic `default-N` ids are also accepted for designers who haven't
   * customised their replies yet.
   */
  quickReplyId?: string;
}

export interface ListGuestMessagesInput {
  threadId: string;
  guestToken: string;
  /** ISO timestamp — only return messages with `created_at > after`. */
  after?: string;
  /** Default 50, max 200. */
  limit?: number;
}

export async function createGuestThread(
  input: CreateGuestThreadInput,
): Promise<{ threadId: string }> {
  const res = (await apiClient.post('/messaging/guest/threads', input)) as ApiResponse<{
    threadId: string;
  }>;
  if (!res.success) throw new Error(res.error.message);
  return res.data;
}

export async function sendGuestMessage(
  input: SendGuestMessageInput,
): Promise<DbMessage> {
  // Backend expects snake_case — translate just the fields whose names diverge.
  const payload: Record<string, unknown> = {
    threadId: input.threadId,
    guestToken: input.guestToken,
    body: input.body,
    attachments: input.attachments,
    clientTempId: input.clientTempId,
  };
  if (input.quickReplyId) payload.quickReplyId = input.quickReplyId;

  const res = (await apiClient.post(
    '/messaging/guest/messages',
    payload,
  )) as ApiResponse<DbMessage>;
  if (!res.success) throw new Error(res.error.message);
  return res.data;
}

/* ---------- Authed send (used for chip-tap auto-reply path) ----------
 * The standard authed send path is direct Supabase INSERT (`insertClientMessage`
 * / `insertDesignerMessage`) which is fine for ordinary messages. When the
 * buyer taps a Quick Reply chip we have to go through Express so the server
 * can transactionally insert both the buyer's message AND the matching
 * auto-reply. Backend handles synthetic `default-N` ids for not-yet-customised
 * designers.
 */

export interface SendAuthedMessageInput {
  threadId: string;
  body: string;
  attachments?: MessageAttachment[];
  clientTempId?: string;
  quickReplyId?: string;
}

export async function sendAuthedMessage(
  input: SendAuthedMessageInput,
): Promise<DbMessage> {
  const res = (await apiClient.post(
    '/messaging/messages',
    input,
  )) as ApiResponse<DbMessage>;
  if (!res.success) throw new Error(res.error.message);
  return res.data;
}

/**
 * Thrown by `listGuestMessages` when the backend signals that the guest
 * session is dead (410 Gone after auth, 429 rate-limit lockout, etc.) and
 * the caller should permanently stop polling. Callers should treat this as
 * a terminal error: cancel any timers, drop in-memory thread refs, and skip
 * any further requests against the same token.
 *
 * The matching localStorage entry is cleared automatically before the throw
 * so the next mount cannot resurrect the stale token.
 */
export class GuestPollStoppedError extends Error {
  readonly code: string;
  readonly status: number | null;
  readonly stopPolling = true as const;
  constructor(code: string, message?: string, status: number | null = null) {
    super(message ?? code);
    this.name = 'GuestPollStoppedError';
    this.code = code;
    this.status = status;
  }
}

const STOP_POLLING_CODES = new Set(['GUEST_SESSION_ENDED', 'GUEST_POLL_RATE_LIMITED']);

/**
 * Poll for designer replies on a guest thread. RLS blocks anon SELECTs on
 * `messages` directly, so the guest path uses this server-validated endpoint
 * (rate-limited to 30/min/IP, supports a 3–5s polling cadence).
 *
 * The backend returns 410 Gone with `{ code: 'GUEST_SESSION_ENDED',
 * shouldStopPolling: true }` when an authed Bearer token hits the guest
 * endpoint (stale localStorage carry-over after login), and 429 with
 * `{ code: 'GUEST_POLL_RATE_LIMITED', shouldStopPolling: true }` when the IP
 * is throttled. Both signals are surfaced as a `GuestPollStoppedError` so
 * the poll loop can break and clean up.
 */
export async function listGuestMessages(
  input: ListGuestMessagesInput,
): Promise<DbMessage[]> {
  const params: Record<string, string | number> = { token: input.guestToken };
  if (input.after) params.after = input.after;
  if (input.limit) params.limit = input.limit;
  const res = (await apiClient.get(
    `/messaging/guest/threads/${input.threadId}/messages`,
    params,
  )) as ApiResponse<DbMessage[]> & {
    code?: string;
    shouldStopPolling?: boolean;
  };

  // Stop-polling signal: either explicit `shouldStopPolling` flag in the
  // body or a known terminal code. apiClient swallows the HTTP status, so
  // we inspect the body directly. Clear the stale token from localStorage
  // before throwing so a remount can't immediately re-poll the dead thread.
  const bodyCode = (res as { code?: string }).code;
  const bodyStop = (res as { shouldStopPolling?: boolean }).shouldStopPolling === true;
  if (bodyStop || (bodyCode && STOP_POLLING_CODES.has(bodyCode))) {
    try {
      const { guestThreadStorage } = await import('../utils/guestThreadStorage');
      guestThreadStorage.clearByToken(input.guestToken);
    } catch {
      /* localStorage unavailable — nothing we can do, the throw below still
         signals the caller to stop. */
    }
    throw new GuestPollStoppedError(
      bodyCode ?? 'GUEST_POLL_STOPPED',
      (res as { error?: { message?: string }; message?: string }).error?.message ??
        (res as { message?: string }).message,
      // We don't have the HTTP status here (apiClient strips it) — null is
      // fine; callers branch on the error class, not the status.
      null,
    );
  }

  if (!res.success) {
    throw new Error(
      (res as ApiErrorResponse).error?.message ?? 'Failed to fetch guest messages',
    );
  }
  return res.data ?? [];
}

/* ---------- Authed client (signed-in visitor on a designer profile) ---------- */

export interface InsertClientMessageInput {
  threadId: string;
  senderId: string;
  body: string;
  attachments?: MessageAttachment[];
  clientTempId: string;
  metadata?: Record<string, unknown>;
}

/**
 * Resolve (or create) the client thread for the signed-in visitor with this
 * designer. Idempotent — backend reuses an existing `(designer_id, client_id)`
 * row when one already exists. Mirrors `POST /v1/messaging/threads`.
 */
export async function createAuthedThread(input: {
  designerId: string;
}): Promise<DbThread> {
  const res = (await apiClient.post('/messaging/threads', {
    designerId: input.designerId,
  })) as ApiResponse<DbThread | { thread: DbThread }>;
  if (!res.success) throw new Error(res.error.message);
  // Backend may wrap the row under `thread` — handle both shapes for robustness.
  const data = res.data as DbThread | { thread: DbThread };
  return ('thread' in data ? data.thread : data) as DbThread;
}

export async function insertClientMessage(
  input: InsertClientMessageInput,
): Promise<DbMessage> {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      thread_id: input.threadId,
      sender_role: 'client',
      sender_id: input.senderId,
      body: input.body,
      attachments: input.attachments ?? [],
      client_temp_id: input.clientTempId,
      ...(input.metadata ? { metadata: input.metadata } : {}),
    })
    .select('*')
    .single();
  if (error) throw error;
  return data as DbMessage;
}

/** Postgres SQLSTATE raised by the R1.5 BEFORE INSERT trigger when a sender hits 60/min. */
export const RATE_LIMIT_SQLSTATE = '54000';

export const isRateLimitError = (e: unknown): boolean =>
  typeof e === 'object' &&
  e !== null &&
  (e as { code?: string }).code === RATE_LIMIT_SQLSTATE;

/**
 * Merge any guest threads stored locally onto the now-authed user. Safe to
 * call after every sign-in: silently skips invalid/expired tokens. Clears
 * the matching localStorage entry on success so repeated calls are cheap.
 *
 * Backend: `claim_guest_thread(p_guest_token text)` is SECURITY DEFINER and
 * grants the current user as `client_id`, removes `guest_token`, and merges
 * messages into an existing thread when applicable.
 */
export async function claimAllGuestThreads(): Promise<number> {
  // Lazy import to avoid pulling localStorage helpers into modules that don't
  // need them.
  const { guestThreadStorage } = await import('../utils/guestThreadStorage');
  const stored = guestThreadStorage.listAll();
  if (stored.length === 0) return 0;

  let claimed = 0;
  for (const t of stored) {
    try {
      const { error } = await supabase.rpc('claim_guest_thread', {
        p_guest_token: t.guestToken,
      });
      if (error) {
        console.warn('[messaging] claim_guest_thread failed', t.guestToken, error.message);
        continue;
      }
      guestThreadStorage.clearByToken(t.guestToken);
      claimed += 1;
    } catch (e) {
      console.warn('[messaging] claim_guest_thread threw', t.guestToken, e);
    }
  }
  return claimed;
}
